import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import useSubscriptionPlanPrices from 'hooks/useSubscriptionPlanPrices';
import useNextBox from 'hooks/useNextBox';
import { makeList } from 'helpers';
import { LEARN_MORE } from 'utils/legalUrls';
import boxesImg from './images/img-boxes@2x.jpg';
import bestValuePng from './images/badge-best-value@2x.png';
import shapeSvg from './images/shape.svg';
import { Plans } from '../constants';
import { seasonalPerks, annualPerks } from './constants';
import TrialUpgradeDisclaimer from './TrialUpgradeDisclaimer/TrialUpgradeDisclaimer';

const DesktopLayout = ({
  handleChangeActive,
  active,
  onClickUpgrade,
  showAdditionalPerks,
  children,
}) => {
  const { seasonalPrice, annualPrice } = useSubscriptionPlanPrices();
  const seasonalList = makeList(seasonalPerks, [], showAdditionalPerks);
  const annualList = makeList(annualPerks, [], showAdditionalPerks);
  const confirmRef = useRef(null);
  const nextBox = useNextBox();

  const handleSelectPlanAndScroll = (plan) => {
    handleChangeActive(plan);
    confirmRef.current &&
      confirmRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
  };

  return (
    <>
      <section
        className="trial-upgrade__container"
        style={{ backgroundImage: `url(${boxesImg})` }}
      >
        <section className="trial-upgrade">
          <div className="trial-upgrade__right">
            <div className="trial-upgrade__title">
              Ready to Start Your Membership?
            </div>
            <div className="trial-upgrade__subtitle">
              Starting your membership early ensures you start with the box you
              want.
            </div>
            <div className="trial-upgrade__buttons">
              <div
                className={cx('trial-upgrade__buttons__box', {
                  active: active === Plans.SEASONAL,
                })}
                role="button"
                tabIndex="0"
                onClick={() => handleChangeActive(Plans.SEASONAL)}
                onKeyDown={() => handleChangeActive(Plans.SEASONAL)}
              >
                {active === Plans.SEASONAL && (
                  <div className="active-check">
                    <img src={shapeSvg} alt="active" />
                  </div>
                )}
                <div className="trial-upgrade__buttons__title">SEASONAL</div>
                {seasonalPrice && (
                  <div className="trial-upgrade__buttons__price">
                    {`$${seasonalPrice}`}
                  </div>
                )}
                <div className="trial-upgrade__buttons__type">Standard</div>
              </div>
              <div
                className={cx('trial-upgrade__buttons__box', {
                  active: active === Plans.ANNUAL,
                })}
                role="button"
                tabIndex="0"
                onClick={() => handleChangeActive(Plans.ANNUAL)}
                onKeyDown={() => handleChangeActive(Plans.ANNUAL)}
              >
                {active === Plans.ANNUAL && (
                  <div className="active-check">
                    <img src={shapeSvg} alt="active" />
                  </div>
                )}
                <div className="trial-upgrade__buttons__title">ANNUAL</div>
                {annualPrice && (
                  <div className="trial-upgrade__buttons__price spaced">
                    {`$${annualPrice} USD!`}
                  </div>
                )}
              </div>
            </div>
            <div className="trial-upgrade__next">
              <div className="trial-upgrade__next__title">
                {nextBox ? `Next Box: ${nextBox}` : ''}
              </div>
              <TrialUpgradeDisclaimer selected={active} />
              <div className="trial-upgrade__next__button">
                <button
                  data-testid="confirm-purchase"
                  type="button"
                  className="fff-btn-primary"
                  onClick={() => onClickUpgrade()}
                  ref={confirmRef}
                >
                  CONFIRM PURCHASE
                </button>
                {children}
              </div>
              <div className="trial-upgrade__next__additional">
                *Additional $8/box shipping for Canada, Hawaii, Alaska, and
                Puerto Rico. Sales tax may apply (
                <a href={LEARN_MORE} target="_blank" rel="noreferrer">
                  learn more
                </a>
                ).
              </div>
            </div>
          </div>
        </section>
      </section>
      <section className="trial-upgrade-detail">
        <p className="trial-upgrade-detail__title">Let’s Get Started</p>
        <div className="trial-upgrade-detail__cards">
          <div className="trial-upgrade-detail__card">
            <div className="trial-upgrade-detail__card__title">Seasonal</div>
            {seasonalPrice && (
              <div className="trial-upgrade-detail__card__price">
                <span className="sign">$</span>
                {seasonalPrice}
                <span className="unit">/ box</span>
              </div>
            )}
            <div className="trial-upgrade-detail__card__detail">
              Pay Per Seasonal Box
              <br />+ Free Shipping!
            </div>
            <div className="trial-upgrade-detail__card__checklist">
              <ul className="plan-options">
                {seasonalList.map(({ label, check }) => (
                  <li key={label} className={cx({ 'strike-through': !check })}>
                    <span>{label}</span>
                  </li>
                ))}
              </ul>
            </div>
            <button
              data-testid="select-seasonal"
              className="fff-btn-primary"
              type="button"
              onClick={() => handleSelectPlanAndScroll(Plans.SEASONAL)}
            >
              SELECT SEASONAL
            </button>
          </div>
          <div className="trial-upgrade-detail__card">
            <img
              className="trial-upgrade-detail__best-value"
              src={bestValuePng}
              alt="best value"
            />
            <div className="trial-upgrade-detail__card__title">Annual</div>
            {annualPrice && (
              <div className="trial-upgrade-detail__card__price">
                <span className="sign">$</span>
                {annualPrice}
                <span className="unit">/ year</span>
              </div>
            )}
            <div className="trial-upgrade-detail__card__detail">
              $20 Instant Savings
              <br />
              When you Prepay for 4 Boxes
              <br />+ Free Shipping!
            </div>
            <div className="trial-upgrade-detail__card__checklist">
              <ul className="plan-options">
                {annualList.map(({ label, additionalLabel }) => (
                  <li key={label}>{label}</li>
                ))}
              </ul>
            </div>
            <button
              data-testid="select-annual"
              className="fff-btn-primary"
              type="button"
              onClick={() => handleSelectPlanAndScroll(Plans.ANNUAL)}
            >
              SELECT ANNUAL
            </button>
          </div>
        </div>
      </section>
    </>
  );
};

DesktopLayout.defaultProps = {
  handleChangeActive: () => null,
  active: Plans.SEASONAL,
  showAdditionalPerks: false,
};

DesktopLayout.propTypes = {
  handleChangeActive: PropTypes.func,
  active: PropTypes.string,
  showAdditionalPerks: PropTypes.bool,
};

export default DesktopLayout;
